import React, { createContext } from 'react';
import { useImmerReducer } from 'use-immer';
import { ActionT } from '@/types';
import { useReactiveVar } from '@apollo/client';
import { currentUserVar } from '@/graphql/operations/user';

// Organization logos
import aryaLightLogoWithIcon from '@/assets/organization-logos/aryaLightLogoWithIcon.svg';
import aryaDarkLogoWithIcon from '@/assets/organization-logos/aryaDarkLogoWithIcon.svg';
import { LayoutSettings } from '@/types';
import bellhopLogo from '@/assets/organization-logos/bellhop.svg';
import aptdecoLogo from '@/assets/organization-logos/aptdeco.svg';
import aptdecoShortLogo from '@/assets/organization-logos/aptdeco-logo.svg';
import steadyMDLogo from '@/assets/organization-logos/steadymdLogo.svg';
import kHealthLogo from '@/assets/organization-logos/k-health-sky-blue-80px.png';
import kHealthShortLogo from '@/assets/organization-logos/khealthshort.png';
import thriveLogo from '@/assets/organization-logos/thrive-logo.svg';
import foresightLogo from '@/assets/organization-logos/foresight-logo-green.png';
import connectPediatricsLogo from '@/assets/organization-logos/connect-pediatrics.png';
import acpLogo from '@/assets/organization-logos/acp-logo.png';
import vitableLogo from '@/assets/organization-logos/vitable.svg';
import theKeyLogo from '@/assets/organization-logos/thekey-logo.svg';
import teamSelectLogo from '@/assets/organization-logos/teamselect-logo.png';
import aveannaLogo from '@/assets/organization-logos/aveanna-healthcare-logo.svg';
import KCHHLogo from '@/assets/organization-logos/KCHH-logo.svg';
import OptionsLogo from '@/assets/organization-logos/Options-logo.png';
import DCLogo from '@/assets/organization-logos/dc-logo.png';
import PhsLogo from '@/assets/organization-logos/phs-logo.webp';
import intelliChoiceLogo from '@/assets/organization-logos/intellichoice-logo.png';
import aocLogo from '@/assets/organization-logos/angels-of-care-logo.png';
import frhcLogo from '@/assets/organization-logos/family-resource-home-care.png';
import rahLogo from '@/assets/organization-logos/rah-logo.png';

// Sign in logos
import steadyMDSignInLogo from '@/assets/organization-logos/sign-in_logos/logo_steadyMD.png';
import kHealthSignInLogo from '@/assets/organization-logos/sign-in_logos/logo_khealth.png';
import connectSignInLogo from '@/assets/organization-logos/sign-in_logos/logo_connect.png';
import acpSignInLogo from '@/assets/organization-logos/sign-in_logos/logo_acp.png';
import vitableSignInLogo from '@/assets/organization-logos/sign-in_logos/logo_vitable.png';
import theKeySignInLogo from '@/assets/organization-logos/sign-in_logos/logo_thekey.png';
import teamSelectSignInLogo from '@/assets/organization-logos/sign-in_logos/logo_teamselect.png';
import bellHopSignInLogo from '@/assets/organization-logos/sign-in_logos/logo_bellhop.png';
import aptDecoSignInLogo from '@/assets/organization-logos/sign-in_logos/logo_aptdeco.png';
import thriveSignInLogo from '@/assets/organization-logos/sign-in_logos/logo_thrivepediatric.png';
import aveannaSignInLogo from '@/assets/organization-logos/sign-in_logos/aveanna-healthcare-logo.png';
import KCHHSignInLogo from '@/assets/organization-logos/sign-in_logos/KCHH-color-logo.png';
import DCSignInLogo from '@/assets/organization-logos/dc-logo.png';
import PhsSignInLogo from '@/assets/organization-logos/phs-logo.webp';
import intelliChoiceSignInLogo from '@/assets/organization-logos/sign-in_logos/intellichoice-logo.png';
import aocSignInLogo from '@/assets/organization-logos/sign-in_logos/angels-of-care-logo.png';
import frhcSignInLogo from '@/assets/organization-logos/family-resource-home-care.png';
import rahSignInLogo from '@/assets/organization-logos/sign-in_logos/logo_rah.png';
import primeProvidersLogo from '@/assets/organization-logos/prime-providers-logo.png';

export const organizationConfig: Record<
  | 'ARYA'
  | 'Bellhop'
  | 'AptDeco'
  | 'SteadyMD'
  | 'KHealth'
  | 'Thrive Skilled Pediatric Care'
  | 'Foresight Mental Health'
  | 'Connect Pediatrics'
  | 'Advanced Care Partners'
  | 'Vitable Health'
  | 'TheKey'
  | 'Team Select Home Care'
  | 'Smartling'
  | 'Aveanna'
  | 'KidsCare Home Health'
  | 'Options for Senior America'
  | 'Doctors Choice'
  | 'Pediatric Home Services'
  // | 'Right at Home'
  // | 'Family Resource Home Care'
  | 'IntelliChoice Home Care'
  | 'Angels of Care'
  | 'Family Resource Home Care'
  | 'Right at Home Mid-Ohio Valley'
  | 'Right at Home Galveston'
  | 'Right at Home Woodstock'
  | 'Right at Home Boston North'
  | 'Right at Home Rhome'
  | 'Right at Home Northern Lake County'
  | 'Right at Home Northwest Houston'
  | 'Right at Home North Suburban Chicago'
  | 'Prime Home Health'
  | 'Right at Home Asheville'
  | 'Assisting Hands Naples'
  | 'Right at Home South',
  LayoutSettings
> = {
  ARYA: {
    title: {
      name: 'ARYA',
      legalName: 'Arya',
    },
    pieChartColors: ['#0266D1', '#FFC800', '#F67314', '#02D16D', '#BFBFBF'],
    logo: aryaLightLogoWithIcon,
    darkLogo: aryaDarkLogoWithIcon,
    signInLogo: aryaDarkLogoWithIcon,
  },
  Bellhop: {
    title: {
      name: 'Bellhop',
      legalName: 'Bellhop',
    },
    pieChartColors: ['#6994F4', '#16D3A9', '#FFCF56', '#DD614A'],
    logo: bellhopLogo,
    signInLogo: bellHopSignInLogo,
  },
  AptDeco: {
    title: {
      name: 'AptDeco',
      legalName: 'AptDeco',
    },
    pieChartColors: ['#FF5F3D', '#D33919', '#107758', '#712251'],
    logo: aptdecoLogo,
    shortLogo: aptdecoShortLogo,
    signInLogo: aptDecoSignInLogo,
  },
  SteadyMD: {
    title: {
      name: 'SteadyMD',
      legalName: 'SteadyMD',
    },
    logo: steadyMDLogo,
    pieChartColors: ['#173360', '#FF264D', '#404040', '#26AEBC', '#E0E0E0'],
    signInLogo: steadyMDSignInLogo,
  },
  KHealth: {
    title: {
      name: 'KHealth',
      legalName: 'KHealth',
    },
    pieChartColors: ['#D1FAEC', '#00244D', '#FDAFAF', '#1458A4', '#00406B'],
    logo: kHealthLogo,
    shortLogo: kHealthShortLogo,
    signInLogo: kHealthSignInLogo,
  },
  'Thrive Skilled Pediatric Care': {
    title: {
      name: 'Thrive',
      legalName: 'Thrive Skilled Pediatric Care',
    },
    pieChartColors: ['#0076BC', '#E02827', '#82BD41', '#81BD41', '#7DAED3'],
    logo: thriveLogo,
    signInLogo: thriveSignInLogo,
  },
  'Foresight Mental Health': {
    title: {
      name: 'Foresight',
      legalName: 'Foresight Mental Health',
    },
    pieChartColors: ['#FF8046', '#163915', '#F67314', '#02D16D', '#BFBFBF'],
    logo: foresightLogo,
    signInLogo: foresightLogo,
  },
  'Connect Pediatrics': {
    title: {
      name: 'Connect Pediatrics',
      legalName: 'Connect Pediatrics',
    },
    pieChartColors: ['#4FB9B9', '#263D3D', '#D1FAEC', '#0266D1', '#BFBFBF'],
    logo: connectPediatricsLogo,
    signInLogo: connectSignInLogo,
  },
  'Advanced Care Partners': {
    title: {
      name: 'Advanced Care Partners',
      legalName: 'Advanced Care Partners',
    },
    pieChartColors: ['#207BB7', '#f29945', '#edf7f9', '#0266D1', '#BFBFBF'],
    logo: acpLogo,
    signInLogo: acpSignInLogo,
  },
  'Vitable Health': {
    title: {
      name: 'Vitable Health',
      legalName: 'Vitable Health',
    },
    pieChartColors: ['#56b7a9', '#355b65', '#d8f1eb', '#a79dec', '#BFBFBF'],
    logo: vitableLogo,
    signInLogo: vitableSignInLogo,
  },
  TheKey: {
    title: {
      name: 'TheKey',
      legalName: 'TheKey',
    },
    pieChartColors: ['#56b7a9', '#355b65', '#d8f1eb', '#a79dec', '#BFBFBF'],
    logo: theKeyLogo,
    signInLogo: theKeySignInLogo,
  },
  'Team Select Home Care': {
    title: {
      name: 'Team Select Home Care',
      legalName: 'Team Select Home Care',
    },
    pieChartColors: ['#56b7a9', '#355b65', '#d8f1eb', '#a79dec', '#BFBFBF'],
    logo: teamSelectLogo,
    signInLogo: teamSelectSignInLogo,
  },
  Smartling: {
    title: {
      name: 'Arya Smartling',
      legalName: 'Arya Smartling',
    },
    pieChartColors: ['#0266D1', '#FFC800', '#F67314', '#02D16D', '#BFBFBF'],
    logo: aryaLightLogoWithIcon,
    darkLogo: aryaDarkLogoWithIcon,
    signInLogo: aryaDarkLogoWithIcon,
  },
  Aveanna: {
    title: {
      name: 'Aveanna',
      legalName: 'Aveanna Healthcare,LLC',
    },
    pieChartColors: ['#0077bc', '#047d96', '#06866b', '#02D16D', '#BFBFBF'],
    logo: aveannaLogo,
    signInLogo: aveannaSignInLogo,
  },
  'KidsCare Home Health': {
    title: {
      name: 'KidsCare',
      legalName: 'KidsCare Home Health',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: KCHHLogo,
    signInLogo: KCHHSignInLogo,
  },
  'Options for Senior America': {
    title: {
      name: 'Options',
      legalName: 'Options for Senior America',
    },
    pieChartColors: ['#12597D', '#BF510D', '#038087', '#BF510D', '#045a47'],
    logo: OptionsLogo,
    signInLogo: OptionsLogo,
  },
  'Doctors Choice': {
    title: {
      name: 'Doctors Choice',
      legalName: 'Doctors Choice',
    },
    pieChartColors: ['#ab915f', '#1a2235', '#038087', '#BF510D', '#045a47'],
    logo: DCLogo,
    signInLogo: DCSignInLogo,
  },
  'Pediatric Home Services': {
    title: {
      name: 'Pediatric Home Services',
      legalName: 'Pediatric Home Services',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: PhsLogo,
    signInLogo: PhsSignInLogo,
  },
  // Revert back to Right at Home after demo
  'Angels of Care': {
    title: {
      name: 'Angels of Care',
      legalName: 'Angels of Care',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: aocLogo,
    signInLogo: aocSignInLogo,
  },
  'IntelliChoice Home Care': {
    title: {
      name: 'IntelliChoice Home Care',
      legalName: 'IntelliChoice Home Care',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: intelliChoiceLogo,
    signInLogo: intelliChoiceSignInLogo,
  },
  'Family Resource Home Care': {
    title: {
      name: 'Family Resource Home Care',
      legalName: 'Family Resource Home Care',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: frhcLogo,
    signInLogo: frhcSignInLogo,
  },
  'Right at Home Mid-Ohio Valley': {
    title: {
      name: 'Right at Home Mid-Ohio Valley',
      legalName: 'Right at Home Mid-Ohio Valley',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: rahLogo,
    signInLogo: rahSignInLogo,
  },
  'Right at Home Galveston': {
    title: {
      name: 'Right at Home Galveston',
      legalName: 'Right at Home Galveston',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: rahLogo,
    signInLogo: rahSignInLogo,
  },
  'Right at Home Woodstock': {
    title: {
      name: 'Right at Home Woodstock',
      legalName: 'Right at Home Woodstock',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: rahLogo,
    signInLogo: rahSignInLogo,
  },
  'Right at Home Boston North': {
    title: {
      name: 'Right at Home Boston North',
      legalName: 'Right at Home Boston North',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: rahLogo,
    signInLogo: rahSignInLogo,
  },
  'Right at Home Rhome': {
    title: {
      name: 'Right at Home Rhome',
      legalName: 'Right at Home Rhome',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: rahLogo,
    signInLogo: rahSignInLogo,
  },
  'Right at Home Northern Lake County': {
    title: {
      name: 'Right at Home Northern Lake County',
      legalName: 'Right at Home Northern Lake County',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: rahLogo,
    signInLogo: rahSignInLogo,
  },
  'Right at Home Northwest Houston': {
    title: {
      name: 'Right at Home Northwest Houston',
      legalName: 'Right at Home Northwest Houston',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: rahLogo,
    signInLogo: rahSignInLogo,
  },
  'Right at Home North Suburban Chicago': {
    title: {
      name: 'Right at Home North Suburban Chicago',
      legalName: 'Right at Home North Suburban Chicago',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: rahLogo,
    signInLogo: rahSignInLogo,
  },
  'Prime Home Health': {
    title: {
      name: 'Prime Home Health',
      legalName: 'Prime Home Health',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: primeProvidersLogo,
    signInLogo: primeProvidersLogo,
  },
  'Right at Home Asheville': {
    title: {
      name: 'Right at Home Asheville',
      legalName: 'Right at Home Asheville',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: rahLogo,
    signInLogo: rahSignInLogo,
  },
  'Assisting Hands Naples': {
    title: {
      name: 'Assisting Hands Naples',
      legalName: 'Assisting Hands Naples',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: 'http://cdn.mcauto-images-production.sendgrid.net/42b1640a6bc21fc4/90321572-78e6-451c-b1fb-334a8fc69f99/268x164.png',
    signInLogo:
      'http://cdn.mcauto-images-production.sendgrid.net/42b1640a6bc21fc4/90321572-78e6-451c-b1fb-334a8fc69f99/268x164.png',
  },
  'Right at Home South': {
    title: {
      name: 'Right at Home South',
      legalName: 'Right at Home South',
    },
    pieChartColors: ['#0077b9', '#e43224', '#1a5a8f', '#c41e1a', '#045a47'],
    logo: rahLogo,
    signInLogo: rahSignInLogo,
  },
};

export const whiteLabelingOrganisations = [
  'AptDeco',
  'SteadyMD',
  'Bellhop',
  'KHealth',
  'Thrive Skilled Pediatric Care',
  'Foresight Mental Health',
  'Connect Pediatrics',
  'Advanced Care Partners',
  'Vitable Health',
  'TheKey',
  'Team Select Home Care',
  'Smartling',
  'Aveanna',
  'KidsCare Home Health',
  'Options for Senior America',
  'Doctors Choice',
  'Pediatric Home Services',
  'Right at Home',
  // Revert back to Right at Home after demo
  'Angels of Care',
  'IntelliChoice Home Care',
  'Family Resource Home Care',
  'Right at Home Mid-Ohio Valley',
  'Right at Home Galveston',
  'Right at Home Woodstock',
  'Right at Home Boston North',
  'Right at Home Rhome',
  'Right at Home Northern Lake County',
  'Right at Home Northwest Houston',
  'Right at Home North Suburban Chicago',
  'Prime Home Health',
  'Right at Home Asheville',
  'Assisting Hands Naples',
  'Right at Home South',
];

export interface ILayoutContext {
  // props
  isMobile?: boolean;
  layoutConfig: LayoutSettings;
  itemsNeedingActionCount?: number;

  // methods
  setItemsNeedingActionCount?: (value: number) => void;
}

const layoutContext = createContext<ILayoutContext>({
  layoutConfig: organizationConfig['ARYA'],
  itemsNeedingActionCount: 0,
});

type State = {
  itemsNeedingActionCount: number;
};

type Action = ActionT<'SET_ITEMS_NEEDING_ACTION_COUNT', State['itemsNeedingActionCount']>;

function reducer(draft: State, action: Action) {
  switch (action.type) {
    case 'SET_ITEMS_NEEDING_ACTION_COUNT':
      draft.itemsNeedingActionCount = action.payload;
      return;
  }
}

type AdminLayoutProviderProps = React.PropsWithChildren;

export const AdminLayoutProvider: React.FC<AdminLayoutProviderProps> = ({ children }) => {
  const initialState: State = {
    itemsNeedingActionCount: 0,
  };

  const [state, dispatch] = useImmerReducer(reducer, initialState);

  const setItemsNeedingActionCount = (value: number) => {
    dispatch({ type: 'SET_ITEMS_NEEDING_ACTION_COUNT', payload: value });
  };

  const currentUser = useReactiveVar(currentUserVar);
  const orgName = currentUser.organization.name as keyof typeof organizationConfig;

  return (
    <layoutContext.Provider
      value={{
        layoutConfig: !whiteLabelingOrganisations.includes(orgName)
          ? organizationConfig['ARYA']
          : organizationConfig[orgName],
        ...state,
        setItemsNeedingActionCount,
      }}
    >
      {children}
    </layoutContext.Provider>
  );
};

export default layoutContext;
